import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface Teacher {
    userId: string;
    teacherName: string;
    email: string;
}

interface TeacherResponse {
    status: boolean;
    message: string;
    data: Teacher[];
}

@Component({
    selector: 'app-assign-teacher-dialog',
    standalone: true,
    templateUrl: './assign-teacher-dialog.component.html',
    styleUrls: ['./assign-teacher-dialog.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
})
export class AssignTeacherDialogComponent implements OnInit {
    form!: FormGroup;
    loading = false;

    // Teacher data
    allTeachers: Teacher[] = [];
    availableTeachers: Teacher[] = [];
    selectedTeachers: Teacher[] = [];

    // Custom select properties
    isDropdownOpen = false;
    searchText = '';
    filteredTeachers: Teacher[] = [];

    // ViewChild for the search input
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

    constructor(
        private fb: FormBuilder,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<AssignTeacherDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            school: any;
            schoolIndex?: number;
            schoolType?: 'mou' | 'nonMou';
            initialData?: any;
        }
    ) {
        this.initForm();
    }
    schoolName = null;

    ngOnInit(): void {
        this.schoolName = this.data?.school?.label;
        const teachersObservable = this.fetchTeachers();
        if (teachersObservable) {
            teachersObservable.subscribe({
                next: () => {
                    this.autoOpenDropdown();
                },
                error: () => {
                    this.autoOpenDropdown();
                }
            });
        } else {
            this.autoOpenDropdown();
        }
    }

    private autoOpenDropdown(): void {
        setTimeout(() => {
            if (this.availableTeachers.length > 0 && !this.isDropdownOpen) {
                this.openDropdown();
            }
        }, 20);
    }

    private initForm(): void {
        this.form = this.fb.group({
        });
    }

    // Custom Select Methods
    openDropdown(): void {
        if (!this.isDropdownOpen) {
            this.isDropdownOpen = true;
            this.filterTeachers();

            // Use a slightly longer timeout and check if input is available
            setTimeout(() => {
                if (this.searchInput && this.searchInput.nativeElement) {
                    this.searchInput.nativeElement.focus();
                }
            }, 50);
        }
    }

    handleInputFocus(event: FocusEvent): void {
        event.stopPropagation();
        if (!this.isDropdownOpen) {
            this.openDropdown();
        }
    }

    filterTeachers(): void {
        if (!this.searchText || this.searchText.trim() === '') {
            // When search is empty, show all available teachers
            this.filteredTeachers = [...this.availableTeachers];
        } else {
            const searchLower = this.searchText.toLowerCase().trim();

            // Filter from available teachers but include all fields for search
            this.filteredTeachers = this.availableTeachers.filter(teacher => {
                // Check teacher name
                const teacherNameMatch = teacher.teacherName?.toLowerCase().includes(searchLower) || false;

                // Check email
                const emailMatch = teacher.email?.toLowerCase().includes(searchLower) || false;

                // Return true if either matches
                return teacherNameMatch || emailMatch;
            });
        }
    }

    selectTeacher(teacher: Teacher): void {
        // Add teacher to selected list
        if (!this.selectedTeachers.find(t => t.userId === teacher.userId)) {
            this.selectedTeachers.push(teacher);

            // Remove from available teachers
            this.availableTeachers = this.availableTeachers.filter(t => t.userId !== teacher.userId);

            // Clear search and update filtered list
            this.searchText = '';
            this.filterTeachers();
        }

        // Close dropdown
        this.isDropdownOpen = false;

        // Re-focus on input after selecting a teacher
        // setTimeout(() => {
        //     if (this.searchInput && this.searchInput.nativeElement) {
        //         this.searchInput.nativeElement.focus();
        //     }
        // }, 10);
    }

    addTeacher(teacher: Teacher): void {
        if (!this.selectedTeachers.find(t => t.userId === teacher.userId)) {
            this.selectedTeachers.push(teacher);
            this.availableTeachers = this.availableTeachers.filter(t => t.userId !== teacher.userId);
            this.filterTeachers();
        }
    }

    removeTeacher(index: number): void {
        const teacher = this.selectedTeachers[index];
        this.selectedTeachers.splice(index, 1);

        // Add back to available teachers if not already there
        if (!this.availableTeachers.find(t => t.userId === teacher.userId)) {
            this.availableTeachers.push(teacher);
            this.filterTeachers();
        }

        // Re-focus on input after removing a teacher
        setTimeout(() => {
            if (this.searchInput && this.searchInput.nativeElement) {
                this.searchInput.nativeElement.focus();
            }
        }, 10);
    }

    getTeacherLabel(teacher: Teacher): string {
        if (!teacher) return '';
        return `${teacher.teacherName}`;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Check if the click is inside our custom select container
        const isClickInsideSelectContainer = target.closest('.custom-select-container');
        const isClickOnInput = target.classList.contains('search-input');

        if (!isClickInsideSelectContainer && !isClickOnInput) {
            this.isDropdownOpen = false;
        }
    }

    fetchTeachers() {
        const schoolId = this.data?.school?.value || this.data?.school?.userId || this.data?.school;

        if (!schoolId) {
            this.showErrorToast('School information is missing');
            return;
        }

        return this.httpService.get<TeacherResponse>(`teachers/dropdown/${schoolId}`).pipe(
            tap({
                next: (response) => {
                    if (Array.isArray(response.data)) {
                        this.allTeachers = response.data.map(item => ({
                            userId: item.userId,
                            teacherName: item.teacherName,
                            email: item.email
                        }));

                        this.availableTeachers = [...this.allTeachers];
                        this.filteredTeachers = [...this.availableTeachers];

                        if (this.data.initialData && Array.isArray(this.data.initialData.teachers)) {
                            this.processInitialTeachers(this.data.initialData.teachers);
                        }
                    } else {
                        this.showErrorToast('Invalid teacher data format');
                    }
                },
                error: (error) => {
                    console.error('Error fetching teachers:', error);
                    this.showErrorToast(error?.message || 'Failed to fetch teachers');
                }
            }),
            finalize(() => {
                this.loading = false;
            })
        );
    }

    private processInitialTeachers(initialTeachers: any[]): void {
        initialTeachers.forEach(teacherData => {
            const teacherId = teacherData.userId || teacherData.teacherId || teacherData.value;
            const teacher = this.allTeachers.find(t => t.userId === teacherId);

            if (teacher) {
                this.selectedTeachers.push(teacher);
                this.availableTeachers = this.availableTeachers.filter(t => t.userId !== teacherId);
            }
        });
        this.filterTeachers();
    }

    onCancel(): void {
        this.dialogRef.close({ success: false });
    }

    onSave(): void {
        this.dialogRef.close({
            success: true,
            teachers: this.selectedTeachers,
            schoolIndex: this.data.schoolIndex,
            schoolType: this.data.schoolType,
            school: this.data.school
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}